import { IAccount } from '~/types'
import { EvmAddress } from '~/utils/address'
import { reverseENSLookup } from '~/utils/ens'
import { captureException } from '~/utils/errors'

// Coalesce concurrent reverse-ENS lookups into one reverseRecords.getNames
// multicall. React Query dedups identical query keys; this dedups *distinct*
// addresses requested within the same debounce window.
const ENS_BATCH_DEBOUNCE_MS = 500

type Resolver = (name: string | undefined) => void

const queue = new Map<string, { account: IAccount; resolvers: Resolver[] }>()
let timer: ReturnType<typeof setTimeout> | null = null

const flush = async () => {
  timer = null
  const batch = [...queue.values()]
  queue.clear()

  try {
    // reverseENSLookup preserves account order, so index alignment is safe
    const names = await reverseENSLookup(batch.map((b) => b.account))
    batch.forEach((entry, index) =>
      entry.resolvers.forEach((resolve) =>
        resolve(names?.[index] ?? undefined),
      ),
    )
  } catch (e) {
    captureException(e)
    batch.forEach((entry) =>
      entry.resolvers.forEach((resolve) => resolve(undefined)),
    )
  }
}

export const loadEnsName = (account: IAccount): Promise<string | undefined> =>
  new Promise((resolve) => {
    const key = `${account.chainId}:${EvmAddress.format(account.address)}`
    const existing = queue.get(key)
    if (existing) {
      existing.resolvers.push(resolve)
    } else {
      queue.set(key, { account, resolvers: [resolve] })
    }
    if (!timer) timer = setTimeout(flush, ENS_BATCH_DEBOUNCE_MS)
  })
